import {
  ApolloClient,
  InMemoryCache,
  ApolloLink,
  HttpLink,
  Observable,
  from,
} from "@apollo/client";
import { onError } from "@apollo/client/link/error";
import {
  getValidToken,
  refreshAuthToken,
  signOut,
} from "../utils/authSession";

// REACT_APP_BACKEND_URL is kept as a legacy fallback for older environments.
const API_URL =
  process.env.REACT_APP_GRAPHQL_URL ||
  process.env.REACT_APP_BACKEND_URL ||
  "https://backend.prod.oelement.net/graphql";

const httpLink = new HttpLink({ uri: API_URL });

// Auth middleware — async, awaits valid token before each request
const authMiddleware = new ApolloLink((operation, forward) => {
  return new Observable((observer) => {
    getValidToken()
      .then((token) => {
        operation.setContext(({ headers = {} }) => ({
          headers: {
            ...headers,
            ...(token ? { authorization: `Bearer ${token}` } : {}),
          },
        }));
      })
      .then(() => {
        const subscriber = forward(operation).subscribe({
          next: observer.next.bind(observer),
          error: observer.error.bind(observer),
          complete: observer.complete.bind(observer),
        });
        return () => subscriber.unsubscribe();
      })
      .catch(observer.error.bind(observer));
  });
});

// Error link — retries once on 401/403 with refreshed token
const isAuthFailure = (graphQLErrors, networkError) => {
  const status = networkError?.statusCode || networkError?.status;
  if (status === 401 || status === 403) return true;
  if (graphQLErrors?.some((e) => e.extensions?.code === "UNAUTHENTICATED")) return true;
  return false;
};

const errorLink = onError(({ graphQLErrors, networkError, operation, forward }) => {
  if (!forward || !isAuthFailure(graphQLErrors, networkError)) return;

  const alreadyRetried = Boolean(operation.getContext()?.__authRetryDone);
  if (alreadyRetried) {
    signOut();
    return;
  }

  return new Observable((observer) => {
    let subscriber = null;
    refreshAuthToken()
      .then((newToken) => {
        if (!newToken) throw new Error("token_refresh_failed");
        operation.setContext(({ headers = {}, ...rest }) => ({
          ...rest,
          __authRetryDone: true,
          headers: { ...headers, authorization: `Bearer ${newToken}` },
        }));
        subscriber = forward(operation).subscribe({
          next: observer.next.bind(observer),
          error: observer.error.bind(observer),
          complete: observer.complete.bind(observer),
        });
      })
      .catch(() => {
        signOut();
        observer.error(new Error("auth_refresh_failed"));
      });
    return () => {
      if (subscriber) subscriber.unsubscribe();
    };
  });
});

const client = new ApolloClient({
  link: from([errorLink, authMiddleware, httpLink]),
  cache: new InMemoryCache(),
});

export default client;
